/**
 * ProductPlaceholder — clean monochrome "no image yet" state for product
 * cards / cart / PDP gallery / routine step thumbs when no real photo
 * exists in WP or the bundled JSON.
 *
 * Reads as intentional brand chrome (mono SKU + IMAGE PENDING tag) rather
 * than a missing-asset error. Replaces the old BottleIllustration line-
 * drawing in fallback positions. The bottle SVG is still kept for the
 * intentional decorative spots (homepage hero inset, sustainability
 * diagram).
 */

interface ProductPlaceholderProps {
  /** Display code, e.g. "BP · 23". Falls back to "—" if absent. */
  label?: string;
  /** Smaller variant for dense spots (cart drawer, routine step thumbs). */
  compact?: boolean;
  className?: string;
}

export default function ProductPlaceholder({
  label = "—",
  compact = false,
  className,
}: ProductPlaceholderProps) {
  return (
    <div
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--bp-bg-2)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: compact ? 4 : 10,
        color: "var(--bp-ink-3)",
        textAlign: "center",
        padding: compact ? 6 : 16,
        overflow: "hidden",
      }}
    >
      {!compact && (
        <span
          aria-hidden="true"
          style={{
            display: "block",
            width: 24,
            height: 1,
            background: "var(--bp-ink-3)",
          }}
        />
      )}
      <span
        style={{
          fontFamily: "var(--bp-mono)",
          fontSize: compact ? 10 : 13,
          letterSpacing: "0.16em",
          color: "var(--bp-ink)",
        }}
      >
        {label}
      </span>
      {!compact && (
        <span
          style={{
            fontFamily: "var(--bp-mono)",
            fontSize: 9,
            letterSpacing: "0.20em",
            color: "var(--bp-ink-3)",
            textTransform: "uppercase",
          }}
        >
          Image pending
        </span>
      )}
    </div>
  );
}
